import { useEffect, useRef, useState } from 'react';

import { ehNativo } from 'services/plataforma';
import {
	ATUAL,
	buscarVersaoExigida,
	situacaoDaVersao,
	versaoExigida,
	versaoNaLoja,
} from 'services/versaoApp';

/** Intervalo mínimo entre duas checagens na volta do segundo plano, com o app em dia. */
export const INTERVALO_CHECAGEM = 30 * 60 * 1000;

/**
 * A versão do app diante da exigida pelo backend e da oferecida pela Play (services/versaoApp), checada
 * ao abrir e na volta do segundo plano. Nem em toda requisição, nem só no login: o refresh token mantém
 * a sessão por semanas, e um bloqueio no meio de uma ação seria pior que um na abertura.
 *
 * Com o app em dia, a volta checa no máximo a cada INTERVALO_CHECAGEM. Fora dele, checa a cada volta:
 * bloqueado, para soltar logo se o admin baixar a versão; aguardando a loja, para bloquear logo que a
 * Play oferecer a versão nova.
 *
 * A Play só é consultada quando a instalada está abaixo da exigida: na abertura comum, ninguém pergunta
 * nada à loja.
 *
 * Na web não faz nada: o site é sempre o último publicado.
 *
 * @returns {{ situacao: string, versaoInstalada: string|null }}
 */
export default function useVersaoApp() {
	const [estado, setEstado] = useState({ situacao: ATUAL, versaoInstalada: null });
	const ultimaChecagem = useRef(0);
	const situacaoAtual = useRef(ATUAL);

	useEffect(() => {
		if (!ehNativo()) return undefined;

		let cancelado = false;
		let removerOuvinte;

		const checar = async (App) => {
			ultimaChecagem.current = Date.now();
			try {
				const [info, exigida] = await Promise.all([App.getInfo(), buscarVersaoExigida()]);
				if (cancelado || !exigida) return;
				// No Android, `build` é o versionCode; `version`, o versionName que a pessoa vê.
				const atrasada = Number(info?.build) < versaoExigida(exigida);
				const naLoja = atrasada ? await versaoNaLoja() : null;
				if (cancelado) return;
				const situacao = situacaoDaVersao({ atual: info?.build, ...exigida, naLoja });
				situacaoAtual.current = situacao;
				setEstado({ situacao, versaoInstalada: info?.version ?? null });
			} catch {
				// Sem a versão instalada não há o que comparar: segue sem exigência.
			}
		};

		(async () => {
			try {
				const { App } = await import('@capacitor/app');
				if (cancelado) return;
				checar(App);
				const inscricao = await App.addListener('appStateChange', ({ isActive }) => {
					if (!isActive) return;
					const emDia = situacaoAtual.current === ATUAL;
					if (!emDia || Date.now() - ultimaChecagem.current >= INTERVALO_CHECAGEM) checar(App);
				});
				removerOuvinte = () => inscricao?.remove?.();
				if (cancelado) removerOuvinte();
			} catch {
				// Sem o plugin: nada a checar.
			}
		})();

		return () => {
			cancelado = true;
			removerOuvinte?.();
		};
	}, []);

	return estado;
}
